import { STAFF_IDENTIFY_CLIENT_ID } from "@tovi/shared";
import { baseUrl, signOut, token } from "./api.ts";

/**
 * The till-approval calls (`#213`), against Better Auth's `deviceAuthorization`
 * plugin directly rather than through `api()`.
 *
 * `api()` reads the app's own error envelope (`{ error: { code, message,
 * reason } }`) and would flatten every refusal here to `INTERNAL`: these three
 * endpoints answer in RFC 8628's own `{ error, error_description }` shape, or
 * — for the 403 `hooks.before` raises in `apps/api/src/auth.ts` — Better
 * Auth's own `APIError` body. `api()`'s one rule survives here by hand
 * instead: a 401 signs the terminal out, the same way a dead token anywhere
 * else in this app does.
 */
async function deviceFetch(path: string, init: RequestInit): Promise<Response> {
  const held = token();
  const response = await fetch(`${baseUrl()}/api${path}`, {
    ...init,
    headers: {
      ...init.headers,
      ...(held ? { Authorization: `Bearer ${held}` } : {}),
    },
  });
  if (response.status === 401) signOut();
  return response;
}

export type ClaimResult =
  | { kind: "ready" }
  | { kind: "notForTill" }
  | { kind: "taken" }
  | { kind: "used" }
  | { kind: "gone" }
  | { kind: "refused" }
  | { kind: "offline" };

/**
 * Claims `userCode` for the caller and says what came back.
 *
 * `GET /device` claims an unclaimed code for whoever calls it with a bearer,
 * then answers `client_id` and `scope` only to the account holding the claim
 * — so a `pending` code with no `client_id` in the body means somebody else
 * already holds it, not that the code is unclaimed (`#213`'s own read of
 * `better-auth/dist/plugins/device-authorization/routes.mjs`).
 */
export async function claimCode(userCode: string): Promise<ClaimResult> {
  let response: Response;
  try {
    response = await deviceFetch(`/auth/device?user_code=${encodeURIComponent(userCode)}`, {
      method: "GET",
    });
  } catch {
    return { kind: "offline" };
  }

  if (response.status === 400) return { kind: "gone" };
  if (response.status === 403) return { kind: "refused" };
  if (!response.ok) return { kind: "offline" };

  const body = (await response.json().catch(() => null)) as {
    status?: string;
    client_id?: string;
  } | null;
  if (!body?.status) return { kind: "offline" };

  if (body.status !== "pending") return { kind: "used" };
  if (!body.client_id) return { kind: "taken" };
  return body.client_id === STAFF_IDENTIFY_CLIENT_ID ? { kind: "ready" } : { kind: "notForTill" };
}

export type DeviceActionResult =
  { kind: "done" } | { kind: "gone" } | { kind: "refused" } | { kind: "offline" };

async function postDeviceAction(path: string, userCode: string): Promise<DeviceActionResult> {
  let response: Response;
  try {
    response = await deviceFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userCode }),
    });
  } catch {
    return { kind: "offline" };
  }

  if (response.status === 200) return { kind: "done" };
  if (response.status === 400) return { kind: "gone" };
  if (response.status === 403) return { kind: "refused" };
  return { kind: "offline" };
}

export function approveCode(userCode: string): Promise<DeviceActionResult> {
  return postDeviceAction("/auth/device/approve", userCode);
}

export function denyCode(userCode: string): Promise<DeviceActionResult> {
  return postDeviceAction("/auth/device/deny", userCode);
}
